<template>
  <section class="pricing lp-section" :class="{ 'pricing--embedded': embedded }">
    <div class="lp-container">
      <!-- Header -->
      <header class="pricing-head">
        <div class="lp-eyebrow"><span class="lp-eyebrow__dot"></span>{{ $t('pricing.badge') }}</div>
        <h2 class="lp-h2 lp-balance">
          {{ $t('pricing.title1') }}
          <span class="lp-accent-text">{{ $t('pricing.titleHighlight') }}</span>
        </h2>

        <!-- Billing toggle -->
        <div class="billing-toggle" role="tablist">
          <button
            class="billing-opt"
            :class="{ active: !isAnnual }"
            @click="isAnnual = false"
          >{{ $t('pricing.monthly') }}</button>
          <button
            class="billing-opt"
            :class="{ active: isAnnual }"
            @click="isAnnual = true"
          >
            {{ $t('pricing.yearly') }}
            <span class="save-tag">{{ $t('pricing.save') }}</span>
          </button>
        </div>
      </header>

      <!-- Plans -->
      <div class="plans">
        <div
          v-for="plan in planCards"
          :key="plan.id"
          class="plan"
          :class="{ 'plan--featured': plan.id === 'pro' }"
        >
          <div v-if="plan.id === 'pro'" class="plan-flag">{{ $t('pricing.proBadge') }} · {{ popularLabel }}</div>

          <div class="plan-head">
            <h3 class="plan-name">{{ plan.label }}</h3>
          </div>

          <div class="plan-price">
            <span class="plan-currency">€</span>
            <span class="plan-amount">{{ plan.displayPrice }}</span>
            <span class="plan-period">{{ plan.periodLabel }}</span>
          </div>

          <button
            class="lp-btn plan-cta"
            :class="plan.id === 'pro' ? 'lp-btn--primary' : 'lp-btn--ghost'"
            :disabled="busy === plan.id"
            @click="onPlanAction(plan.id)"
          >
            <span v-if="busy === plan.id">…</span>
            <span v-else>{{ planButtonLabel(plan.id) }}</span>
          </button>

          <ul class="plan-features">
            <li v-for="(feature, i) in plan.features" :key="i">
              <l-icon size="16" :color="plan.id === 'pro' ? 'primary' : ''" class="plan-check">mdi-check</l-icon>
              <span>{{ feature }}</span>
            </li>
          </ul>
        </div>
      </div>

      <div v-if="isAuthenticated && currentPlan !== 'free'" class="manage-row">
        <button class="link-btn" :disabled="busy === 'portal'" @click="openPortal">{{ $t('pricing.manage') }}</button>
      </div>

      <p class="guarantee">
        <l-icon size="16">mdi-shield-check</l-icon>
        {{ $t('pricing.guarantee') }} · {{ $t('pricing.guaranteeDesc') }}
      </p>

      <!-- FAQ -->
      <div v-if="!embedded" class="faq">
        <h3 class="lp-h3 faq-title">{{ $t('pricing.faqTitle') }}</h3>
        <div class="faq-list">
          <details v-for="(item, i) in faqItems" :key="i" class="faq-item">
            <summary>
              {{ item.q }}
              <l-icon size="18" class="faq-chevron">mdi-chevron-down</l-icon>
            </summary>
            <p>{{ item.a }}</p>
          </details>
        </div>
        <p class="faq-contact">
          {{ $t('pricing.contact') }}
          <a href="mailto:hola@nukliapp.com">hola@nukliapp.com</a>
        </p>
      </div>
    </div>
  </section>
</template>

<script>
import { ALL_PLANS, PAID_PLANS, planPrice } from '@/constants/plans';
import { billingCheckout, billingPortal, getBillingStatus, getSession } from '@/services/nukliApi';

export default {
  name: 'PricingSection',
  props: {
    embedded: { type: Boolean, default: false },
  },
  data: () => ({
    // Mensual por defecto: es el compromiso más bajo y el precio que el visitante
    // espera ver primero; el anual queda a un clic con su descuento visible.
    isAnnual: false,
    busy: null,
    billing: null,
  }),
  computed: {
    popularLabel() {
      return this.$i18n.locale === 'es' || this.$i18n.locale === 'ca' ? 'Más popular' : 'Most popular';
    },
    isAuthenticated() {
      return getSession().isAuthenticated;
    },
    currentPlan() {
      return this.billing?.plan || 'free';
    },
    faqItems() {
      return this.$t('pricing.faq');
    },
    planCards() {
      const interval = this.isAnnual ? 'year' : 'month';
      const badges = {
        free: this.$t('pricing.freeBadge'),
        pro: this.$t('pricing.proBadge'),
        pro_plus: this.$t('pricing.proPlusBadge'),
        ultra: this.$t('pricing.ultraBadge'),
      };
      const labels = {
        free: this.$t('pricing.free'),
        pro: this.$t('pricing.pro'),
        pro_plus: this.$t('pricing.proPlus'),
        ultra: this.$t('pricing.ultra'),
      };
      return ALL_PLANS.map((id) => {
        const price = planPrice(id, interval);
        const isYear = this.isAnnual && id !== 'free';
        return {
          id,
          label: labels[id],
          badge: badges[id],
          displayPrice: id === 'free' ? '0' : (isYear ? Math.round(price) : price.toFixed(2).replace('.00', '')),
          periodLabel: id === 'free' ? this.$t('pricing.freePeriod') : (isYear ? this.$t('pricing.perYear') : this.$t('pricing.perMonth')),
          features: this.$t(`plans.${id}.features`),
        };
      });
    },
  },
  mounted() {
    this.loadBilling();
    if (this.$route.query.billing === 'success') {
      this.$toast.success('Subscription updated!');
      this.loadBilling();
    }
  },
  methods: {
    async loadBilling() {
      if (!this.isAuthenticated) return;
      try {
        this.billing = await getBillingStatus();
      } catch {
        this.billing = null;
      }
    },
    isCurrentPlan(planId) {
      return this.currentPlan === planId;
    },
    planButtonLabel(planId) {
      if (planId === 'free') return this.$t('pricing.freeCta');
      if (this.isCurrentPlan(planId)) return this.$t('pricing.currentPlan');
      if (!this.isAuthenticated) return this.$t('pricing.signInFirst');
      const labels = { pro: this.$t('pricing.pro'), pro_plus: this.$t('pricing.proPlus'), ultra: this.$t('pricing.ultra') };
      return this.$t('pricing.upgrade', { plan: labels[planId] });
    },
    async onPlanAction(planId) {
      if (planId === 'free') {
        this.$emit('navigate');
        this.$router.push('/download').catch(() => {});
        return;
      }
      if (!this.isAuthenticated) {
        this.$emit('navigate');
        this.$router.push({ path: '/login', query: { redirect: '/#pricing' } }).catch(() => {});
        return;
      }
      if (this.isCurrentPlan(planId)) return;
      if (!PAID_PLANS.includes(planId)) return;

      this.busy = planId;
      try {
        const interval = this.isAnnual ? 'year' : 'month';
        const { url } = await billingCheckout(planId, interval);
        if (url) window.location.href = url;
      } catch (err) {
        this.$toast.error(err.message || 'Checkout failed');
      } finally {
        this.busy = null;
      }
    },
    async openPortal() {
      this.busy = 'portal';
      try {
        const { url } = await billingPortal();
        if (url) window.location.href = url;
      } catch (err) {
        this.$toast.error(err.message || 'Portal unavailable');
      } finally {
        this.busy = null;
      }
    },
  },
};
</script>

<style scoped>
.pricing { background: var(--lp-bg); }
.pricing--embedded { padding: 44px 0 52px; }
.pricing--embedded .pricing-head { margin-bottom: 40px; }
@media (max-width: 599px) {
  .pricing--embedded { padding: 28px 0 36px; }
}

.pricing-head {
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 18px;
  margin-bottom: 56px;
}

/* Billing toggle */
.billing-toggle {
  display: inline-flex;
  padding: 4px;
  border: 1px solid var(--lp-line);
  border-radius: 12px;
  background: var(--lp-bg-subtle);
  margin-top: 4px;
}
.billing-opt {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  border: 0;
  background: transparent;
  cursor: pointer;
  padding: 9px 18px;
  border-radius: 9px;
  font-family: 'Space Grotesk', sans-serif;
  font-weight: 600;
  font-size: 0.9rem;
  color: var(--lp-muted);
  transition: all 0.15s ease;
}
.billing-opt.active {
  background: var(--lp-surface);
  color: var(--lp-ink);
  box-shadow: var(--lp-shadow-sm);
}
.save-tag {
  font-size: 0.7rem;
  font-weight: 700;
  color: var(--lp-accent);
  background: var(--lp-accent-weak);
  padding: 2px 7px;
  border-radius: 6px;
}

/* Plans */
.plans {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;
  align-items: stretch;
}
.plan {
  position: relative;
  background: var(--lp-surface);
  border: 1px solid var(--lp-line);
  border-radius: var(--lp-radius-lg);
  padding: 28px 24px;
  display: flex;
  flex-direction: column;
}
.plan--featured {
  border-color: var(--lp-accent);
  box-shadow: 0 0 0 1px var(--lp-accent), var(--lp-shadow-lg);
  transform: translateY(-8px);
  z-index: 1;
}
.plan-flag {
  position: absolute;
  top: -12px;
  left: 50%;
  transform: translateX(-50%);
  background: var(--lp-accent);
  color: #fff;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.03em;
  padding: 5px 12px;
  border-radius: 100px;
  white-space: nowrap;
}
.plan-name {
  font-family: 'Space Grotesk', sans-serif;
  font-size: 1.15rem;
  font-weight: 600;
  color: var(--lp-ink);
  margin: 0 0 18px;
}
.plan-price {
  display: flex;
  align-items: baseline;
  gap: 2px;
  margin-bottom: 22px;
}
.plan-currency { font-size: 1.25rem; font-weight: 600; color: var(--lp-ink); align-self: flex-start; margin-top: 6px; }
.plan-amount {
  font-family: 'Space Grotesk', sans-serif;
  font-size: 2.75rem;
  font-weight: 700;
  letter-spacing: -0.03em;
  color: var(--lp-ink);
  line-height: 1;
}
.plan-period { font-size: 0.85rem; color: var(--lp-muted); margin-left: 4px; }

.plan-cta {
  width: 100%;
  height: 44px;
  font-size: 0.95rem;
  margin-bottom: 24px;
}

.plan-features {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 11px;
}
.plan-features li {
  display: flex;
  align-items: flex-start;
  gap: 9px;
  font-size: 0.9rem;
  line-height: 1.4;
  color: var(--lp-ink-2);
}
.plan-check { flex: 0 0 auto; margin-top: 1px; color: var(--lp-muted); }

.manage-row { text-align: center; margin-top: 28px; }
.link-btn {
  border: 0; background: none; cursor: pointer;
  color: var(--lp-accent); font-weight: 600; font-family: 'Space Grotesk', sans-serif;
}

.guarantee {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-top: 36px;
  font-size: 0.875rem;
  color: var(--lp-muted);
  text-align: center;
}

/* FAQ */
.faq { max-width: 760px; margin: 104px auto 0; }
.faq-title { text-align: center; margin-bottom: 36px; }
.faq-list { display: flex; flex-direction: column; }
.faq-item {
  border-bottom: 1px solid var(--lp-line);
  padding: 4px 0;
}
.faq-item summary {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  cursor: pointer;
  list-style: none;
  padding: 20px 4px;
  font-family: 'Space Grotesk', sans-serif;
  font-size: 1.0625rem;
  font-weight: 600;
  color: var(--lp-ink);
  user-select: none;
}
.faq-item summary::-webkit-details-marker { display: none; }
.faq-chevron { color: var(--lp-muted); transition: transform 0.2s ease; flex: 0 0 auto; }
.faq-item[open] .faq-chevron { transform: rotate(180deg); }
.faq-item p {
  margin: 0;
  padding: 0 4px 22px;
  font-size: 1rem;
  line-height: 1.65;
  color: var(--lp-muted);
}
.faq-contact { text-align: center; margin-top: 40px; color: var(--lp-muted); }
.faq-contact a { color: var(--lp-accent); text-decoration: none; font-weight: 600; }

@media (max-width: 959px) {
  .plans { grid-template-columns: repeat(2, 1fr); gap: 16px; }
  .plan--featured { transform: none; }
  .faq { margin-top: 72px; }
}
@media (max-width: 599px) {
  .plans { grid-template-columns: 1fr; }
}
</style>
